import React from 'react';
import ReactDOM from 'react-dom/client';

type LazyImporter = () => Promise<{ default: React.ComponentType<any> } | Record<string, any>>;
type LazyRegistry = Record<string, LazyImporter>;

const registries: LazyRegistry[] = [];
const mountedRoots = new Map<Element, ReactDOM.Root>();
const pendingMounts = new Set<Element>();
const componentCache = new Map<string, React.ComponentType<any>>();
let mountGeneration = 0;

let contextProvider: React.ComponentType<any> | null = null;

export function registerContextProvider(Provider: React.ComponentType<any>): void {
  contextProvider = Provider;
}

export function registerLazyComponents(registry: LazyRegistry): void {
  registries.push(registry);
}

export function buildLazyRegistryFromGlob(
  modules: Record<string, LazyImporter>,
  stripPrefix: string
): LazyRegistry {
  const registry: LazyRegistry = {};

  for (const path in modules) {
    const name = (path.startsWith(stripPrefix) ? path.slice(stripPrefix.length) : path)
      .replace(/\.(tsx|ts|jsx|js)$/, '');
    registry[name] = modules[path];
  }

  return registry;
}

export function buildEagerRegistryFromGlob(
  modules: Record<string, any>,
  stripPrefix: string
): LazyRegistry {
  const registry: LazyRegistry = {};

  for (const path in modules) {
    const name = (path.startsWith(stripPrefix) ? path.slice(stripPrefix.length) : path)
      .replace(/\.(tsx|ts|jsx|js)$/, '');
    const mod = modules[path];
    registry[name] = () => Promise.resolve(mod);
  }

  return registry;
}

function findLazyComponent(className: string): LazyImporter | undefined {
  for (const registry of registries) {
    if (registry[className]) return registry[className];
  }
  return undefined;
}

async function resolveComponent(className: string): Promise<React.ComponentType<any> | undefined> {
  const cached = componentCache.get(className);
  if (cached) return cached;

  const importer = findLazyComponent(className);
  if (!importer) return undefined;

  let mod: Awaited<ReturnType<LazyImporter>>;
  try {
    mod = await importer();
  } catch (e) {
    console.error(`[vite-react-mount] Failed to load module for "${className}":`, e);
    return undefined;
  }
  const component = (mod as any).default || mod;
  if (component) {
    componentCache.set(className, component);
  }
  return component;
}

export async function mountComponents(): Promise<void> {
  const nodes = document.querySelectorAll('[data-react-class]');

  const generation = mountGeneration;

  const mountPromises = Array.from(nodes).map(async (node) => {
    if (mountedRoots.has(node) || pendingMounts.has(node)) return;
    pendingMounts.add(node);

    const className = node.getAttribute('data-react-class');
    const propsJson = node.getAttribute('data-react-props') || '{}';

    if (!className) {
      pendingMounts.delete(node);
      return;
    }

    const Component = await resolveComponent(className);
    if (mountGeneration !== generation) {
      pendingMounts.delete(node);
      return;
    }
    if (!Component) {
      console.error(`[vite-react-mount] Component "${className}" not found in any registry`);
      pendingMounts.delete(node);
      return;
    }

    let props: Record<string, any>;
    try {
      props = JSON.parse(propsJson);
    } catch (e) {
      console.error(`[vite-react-mount] Failed to parse props for "${className}":`, e);
      pendingMounts.delete(node);
      return;
    }

    const contextPropsJson = node.getAttribute('data-react-context-props');
    let ComponentToMount: React.ComponentType<any> = Component;
    if (contextPropsJson && contextProvider) {
      let parsedContextProps: Record<string, any>;
      try {
        parsedContextProps = JSON.parse(contextPropsJson);
      } catch (e) {
        console.error(`[vite-react-mount] Failed to parse context props for "${className}":`, e);
        pendingMounts.delete(node);
        return;
      }
      const Provider = contextProvider;
      const Inner = Component;
      ComponentToMount = function ContextWrapped(innerProps: any) {
        return React.createElement(Provider, parsedContextProps, React.createElement(Inner, innerProps));
      };
      ComponentToMount.displayName = `ContextWrapped(${className})`;
    }

    const root = ReactDOM.createRoot(node as HTMLElement);
    root.render(React.createElement(ComponentToMount, props));
    mountedRoots.set(node, root);
    pendingMounts.delete(node);
  });

  await Promise.all(mountPromises);
}

function unmountComponents(): void {
  mountGeneration++;
  mountedRoots.forEach((root) => root.unmount());
  mountedRoots.clear();
  pendingMounts.clear();
  componentCache.clear();
}

export function setupTurbolinksIntegration(): void {
  let initialized = false;

  document.addEventListener('turbolinks:load', () => {
    if (initialized) {
      mountComponents();
    }
  });

  document.addEventListener('turbolinks:before-render', () => {
    initialized = true;
    unmountComponents();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountComponents());
  } else {
    mountComponents();
  }
}
